import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase, SITE_URL } from '../lib/supabase';
import { isValidEmailShape } from '../lib/workEmail';
import { useAuth } from '../contexts/AuthContext';
import { useEffect } from 'react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [params] = useSearchParams();
  const next = params.get('next') || '/dashboard';
  const isVendorFlow = params.get('vendor') === '1';
  const navigate = useNavigate();
  const { session, loading } = useAuth();

  useEffect(() => {
    if (!loading && session) navigate(next, { replace: true });
  }, [session, loading, navigate, next]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    // Login accepts any email shape (members, vendors, and admins all sign in here).
    // The work-email rule is enforced on /signup, not /login.
    if (!isValidEmailShape(email)) {
      setError("That doesn't look like an email address.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${SITE_URL}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    setSubmitting(false);
    if (error) {
      setError(error.message);
      return;
    }
    setSent(true);
  }

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 80, paddingBottom: 120 }}>
        <div className="container-narrow" style={{ maxWidth: 520 }}>
          <div className="eyebrow" style={{ marginBottom: 22 }}>{isVendorFlow ? 'Owners & managers' : 'Sign in'}</div>
          <h1 className="display" style={{ fontSize: 'clamp(40px, 6vw, 64px)', margin: 0 }}>
            {isVendorFlow ? 'List your spot.' : <>Welcome <span className="serif-italic" style={{ color: 'var(--wine)' }}>back.</span></>}
          </h1>

          {sent ? (
            <div style={{ marginTop: 40, padding: 28, borderRadius: 18, background: 'var(--card)' }}>
              <div className="display" style={{ fontSize: 26, marginBottom: 8 }}>Check your inbox.</div>
              <p style={{ color: 'var(--ink-soft)', margin: 0 }}>
                We sent a magic link to <strong>{email}</strong>. Click it to {isVendorFlow ? 'start onboarding' : 'sign in'}. The link expires in an hour.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ marginTop: 40 }}>
              <label>
                <span className="field-label">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
                <div className="field-hint">
                  {isVendorFlow
                    ? "Use the email you signed up with."
                    : "We'll email you a magic link. Any email works for signing in."}
                </div>
                {error && <div className="field-error">{error}</div>}
              </label>

              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ width: '100%', justifyContent: 'center', marginTop: 24 }}>
                {submitting ? 'Sending…' : 'Email me a link →'}
              </button>
            </form>
          )}

          <p className="muted" style={{ fontSize: 13, marginTop: 32 }}>
            New here? Signing in creates an account. By continuing you agree to our <Link to="#" style={{ textDecoration: 'underline' }}>Terms</Link> and <Link to="#" style={{ textDecoration: 'underline' }}>Privacy Policy</Link>.
          </p>
        </div>
      </section>
      <Footer />
    </>
  );
}
